import React from "react";
import {
    addGlobalContexts,
    newTracker,
    enableActivityTracking,
    trackPageView as snowplowTrackPageView,
    removeGlobalContexts,
    trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import {
    LinkClickTrackingPlugin,
    trackLinkClick as snowplowTrackLinkClick,
} from "@snowplow/browser-plugin-link-click-tracking";

const defaultPagePingConfiguration = {
    minimumVisitLength: 5,
    heartbeatDelay: 20,
};

const tabs = ["news", "community", "esport"] as const;
type TabType = (typeof tabs)[number];
type Page = {
    id: string;
    locale: string;
    tab: TabType;
};
type PageContext = Page & {
    maintenance: boolean;
};
type PageView = {
    title: string;
    page: Page;
};

function init(appId: string, collectorUrl: string) {
    newTracker("sp1", collectorUrl, {
        appId,
        plugins: [LinkClickTrackingPlugin()],
    });
    enableActivityTracking(defaultPagePingConfiguration);
}

function setUser(userId: string | number) {
    addGlobalContexts([
        {
            schema: "iglu:com.supercell/user_identification/jsonschema/1-0-0",
            data: {
                user_id_type: "avatar_id",
                user_id: String(userId),
            },
        },
    ]);
}

const pageContextWithSchema = (context: PageContext) => ({
    schema: "iglu:com.supercell/inbox_page/jsonschema/1-0-1",
    data: context,
});

let lastPageContext: PageContext | null = null;
const addGlobalPageContext = (context: PageContext) => {
    if (lastPageContext) {
        removeGlobalContexts([pageContextWithSchema(lastPageContext)]);
    }
    addGlobalContexts([pageContextWithSchema(context)]);
    lastPageContext = context;
};

const addContextAndTrackPageView = (title: string, context: PageContext) => {
    addGlobalPageContext(context);
    snowplowTrackPageView({
        title,
    });
};

const localeAndTab = /\/(?<locale>\w+)\/(?<tab>\w+)/;
const articleId = /\/(?<locale>\w+)\/(?<tab>\w+)\/(?<articleId>[\w_-]+)\//;

const isSamePageView = (a: PageView, b?: PageView) => {
    if (!b) return false;
    return (
        a.page.id === b.page.id &&
        a.page.locale === b.page.locale &&
        a.page.tab === b.page.tab &&
        a.title === b.title
    );
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

type TrackLinkClickParams = {
    url: string;
    id?: string;
    label?: string;
    additionalInformation?: Record<string, unknown>;
};

const SnowplowContext = React.createContext<{
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
    trackImpression: (id: string, label?: string) => void;
    trackLinkClick: (params: TrackLinkClickParams) => void;
    trackPageView: (title: string, path: string) => void;
    setUser: typeof setUser;
    setMaintenance: (maintenance: boolean) => void;
}>({
    // Default to no op for everything, so we can use the context without provider in scope
    trackButtonClick: noop,
    trackImpression: noop,
    trackLinkClick: noop,
    trackPageView: noop,
    setUser: noop,
    setMaintenance: noop,
});

export const SnowplowContextProvider = ({
    children,
    config,
}: {
    children: JSX.Element | JSX.Element[];
    config: { appId: string | null; collectorUrl: string | null };
}): JSX.Element | null => {
    const [pageViewQueue, setPageViewQueue] = React.useState<PageView[]>([]);
    const [maintenance, setMaintenance] = React.useState<boolean>();
    const pageViews = React.useRef<PageView[]>([]);

    const isConfigured = !!config.appId && !!config.collectorUrl;

    if (config.appId && config.collectorUrl) {
        init(config.appId, config.collectorUrl);
    }

    React.useEffect(() => {
        if (typeof maintenance !== "undefined") {
            sendPageViewsFromQueue(maintenance);
        }
    }, [maintenance]);

    const isAlreadySeen = (pageView: PageView) => {
        if (
            pageViews.current.length > 0 &&
            isSamePageView(
                pageView,
                pageViews.current[pageViews.current.length - 1],
            )
        ) {
            return true;
        }

        pageViews.current = [...pageViews.current, pageView];
        return false;
    };

    const sendPageViewsFromQueue = (maintenance: boolean) => {
        if (pageViewQueue.length > 0) {
            pageViewQueue.forEach(({ title, page }) => {
                addContextAndTrackPageView(title, {
                    maintenance,
                    ...page,
                });
            });
            setPageViewQueue([]);
        }
    };

    const getPageFromPath = (path: string) => {
        const localeAndTabResult = path.match(localeAndTab);
        const articleIdResult = path.match(articleId);

        const locale = localeAndTabResult?.groups?.locale ?? "en";
        const tabFromPath =
            // The new fullscreen Brawl Stars calls the esport tab "esports",
            // so this is a workaround to handle that.
            localeAndTabResult?.groups?.tab === "esports"
                ? "esport"
                : (localeAndTabResult?.groups?.tab ?? "news");

        const tab = tabs.includes(tabFromPath as TabType)
            ? (tabFromPath as TabType)
            : "news";
        let id = ""; // "" means frontpage of news/community/esports

        if (articleIdResult) {
            id = articleIdResult?.groups?.articleId ?? "";
        }

        return {
            locale,
            tab,
            id,
        };
    };

    const trackButtonClick = (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => {
        trackSelfDescribingEvent({
            event: {
                schema: "iglu:com.supercell/button_click/jsonschema/1-0-1",
                data: {
                    button_id: id,
                    // button_click (version 1-0-1) schema has a 40 character limit on button_name
                    button_name: label?.substring(0, 40) ?? "",
                },
            },
            ...(additionalInformation
                ? {
                      context: [
                          {
                              schema: "iglu:com.snowplowanalytics.snowplow/additional_information/jsonschema/1-0-0",
                              data: additionalInformation,
                          },
                      ],
                  }
                : {}),
        });
    };

    const trackImpression = (id: string, label?: string) => {
        trackSelfDescribingEvent({
            event: {
                schema: "iglu:com.supercell/impression/jsonschema/1-0-1",
                data: {
                    component_id: id,
                    // impression (version 1-0-1) schema has a 40 character limit on component_name
                    component_name: label?.substring(0, 40) ?? "",
                },
            },
        });
    };

    const trackLinkClick = ({
        url,
        id,
        label,
        additionalInformation,
    }: TrackLinkClickParams) => {
        snowplowTrackLinkClick({
            targetUrl: url,
            elementId: id,
            elementContent: label,
            ...(additionalInformation
                ? {
                      context: [
                          {
                              schema: "iglu:com.snowplowanalytics.snowplow/additional_information/jsonschema/1-0-0",
                              data: additionalInformation,
                          },
                      ],
                  }
                : {}),
        });
    };

    const trackPageView = (title: string, path: string) => {
        const page = getPageFromPath(path);
        const pageView = { title, page };

        if (isAlreadySeen(pageView)) {
            return;
        }

        if (typeof maintenance === "undefined") {
            setPageViewQueue((queue) => [...queue, pageView]);
        } else {
            addContextAndTrackPageView(title, {
                maintenance,
                ...page,
            });
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ifConfigured = (fn: any) => (isConfigured ? fn : noop);

    return (
        <SnowplowContext.Provider
            value={{
                trackButtonClick: ifConfigured(trackButtonClick),
                trackImpression: ifConfigured(trackImpression),
                trackLinkClick: ifConfigured(trackLinkClick),
                trackPageView: ifConfigured(trackPageView),
                setUser: ifConfigured(setUser),
                setMaintenance,
            }}
        >
            {children}
        </SnowplowContext.Provider>
    );
};

export const useSnowplowContext = () => React.useContext(SnowplowContext);
